// Per-body date-range model. iOS-Health-style window paging: pick a
// granularity (W/M/6M/Y/All), then arrow through history at that grain.
//
// Windows are calendar-aligned so the label reads cleanly:
//   * W   — Monday-anchored week
//   * M   — calendar month ("Apr 2026")
//   * 6M  — calendar half-year ("Jan – Jun 2026" / "Jul – Dec 2026")
//   * Y   — calendar year ("2026")
//   * All — unbounded; no paging
//
// `end` is exclusive so [start, end) is half-open — easy to test with `<`.

import { useEffect, useMemo, useState } from 'react';

export type Granularity = 'W' | 'M' | '6M' | 'Y' | 'All';

export const GRANULARITIES: readonly Granularity[] = ['W', 'M', '6M', 'Y', 'All'];

export interface DateWindow {
  start: number; // ms, inclusive
  end: number;   // ms, exclusive
}

function startOfWeek(d: Date): Date {
  // Monday-anchored. JS getDay(): Sun=0..Sat=6; map Sun→7 so Mon=1.
  const dow = d.getDay() === 0 ? 7 : d.getDay();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - (dow - 1));
}

export function windowForAnchor(granularity: Granularity, anchor: Date): DateWindow {
  switch (granularity) {
    case 'W': {
      const s = startOfWeek(anchor);
      const e = new Date(s); e.setDate(s.getDate() + 7);
      return { start: s.getTime(), end: e.getTime() };
    }
    case 'M': {
      const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
      return { start: s.getTime(), end: e.getTime() };
    }
    case '6M': {
      const half = anchor.getMonth() < 6 ? 0 : 6;
      const s = new Date(anchor.getFullYear(), half, 1);
      const e = new Date(anchor.getFullYear(), half + 6, 1);
      return { start: s.getTime(), end: e.getTime() };
    }
    case 'Y': {
      const s = new Date(anchor.getFullYear(), 0, 1);
      const e = new Date(anchor.getFullYear() + 1, 0, 1);
      return { start: s.getTime(), end: e.getTime() };
    }
    case 'All':
      return { start: Number.NEGATIVE_INFINITY, end: Number.POSITIVE_INFINITY };
  }
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Label widths are kept stable across granularities (no year in the W
// label) so the picker doesn't reflow as the user pages between
// granularities. The picker itself reserves a min-width on the label
// to absorb the remaining variation.
export function windowLabel(granularity: Granularity, anchor: Date): string {
  switch (granularity) {
    case 'W': {
      const s = startOfWeek(anchor);
      const e = new Date(s); e.setDate(s.getDate() + 6);
      const sameMonth = s.getMonth() === e.getMonth();
      if (sameMonth) {
        return `${MONTH_SHORT[s.getMonth()]} ${s.getDate()}–${e.getDate()}`;
      }
      return `${MONTH_SHORT[s.getMonth()]} ${s.getDate()} – ${MONTH_SHORT[e.getMonth()]} ${e.getDate()}`;
    }
    case 'M':  return `${MONTH_SHORT[anchor.getMonth()]} ${anchor.getFullYear()}`;
    case '6M': return `${anchor.getMonth() < 6 ? 'Jan – Jun' : 'Jul – Dec'} ${anchor.getFullYear()}`;
    case 'Y':  return String(anchor.getFullYear());
    case 'All': return 'All time';
  }
}

export function withinWindow(dateMs: number, w: DateWindow): boolean {
  return dateMs >= w.start && dateMs < w.end;
}

/** Parse a 'YYYY-MM-DD' date — or a full ISO datetime ('YYYY-MM-DDThh:mm:ss',
 *  the way the feature timeseries' `applies_to` serializes) — as that calendar
 *  day at LOCAL midnight (ms), matching the calendar-aligned windows above.
 *
 *  Only the leading 10 chars are read. This matters: without the slice a time
 *  suffix lands in the day field as NaN, and `NaN || 1` quietly falls back to
 *  the 1st — collapsing every stamp to the month start, which made the
 *  period_over_period chart sum a whole month into one point. */
export function isoToLocalMs(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getTime();
}

export function isAllTime(granularity: Granularity): boolean {
  return granularity === 'All';
}

// Per-body state for the picker. Default granularity is 'M' (calendar
// month) — most check-in features get a few entries a week, so a month
// shows a useful slice without being mostly empty. The anchor starts at
// "today" and snaps to the latest data point once data arrives, unless
// the user has already paged the picker (then we leave it alone).
//
// `initialGranularity` may also arrive LATE and change — a summary card fetches
// the granularity it was last left on (see `useSummaryCardPrefs`), which lands
// after mount. It's adopted under the same rule as the anchor snap: only until
// the user works the picker themselves. So the opening granularity can be
// supplied asynchronously without a caller having to reach in and override the
// hook's state (which would read as a user action and stop the anchor snap).
//
// The hook is data-aware: it takes the full list of data timestamps so
// the prev/next pagers can skip empty windows entirely, jumping straight
// to the next/previous window that actually contains data.
export interface TimeRangeState {
  granularity: Granularity;
  anchor: Date;
  setRange: (g: Granularity, a: Date) => void;
  pagePrev: () => void;
  pageNext: () => void;
  goEarliest: () => void;
  goLatest: () => void;
  canPrev: boolean;
  canNext: boolean;
  isAtLatest: boolean;
  hasData: boolean;
}

export function useTimeRangeState(
  dataDates: number[],
  initialGranularity: Granularity = 'M',
): TimeRangeState {
  const [granularity, setGranularity] = useState<Granularity>(initialGranularity);
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [userTouched, setUserTouched] = useState(false);

  // Adopt a late-arriving opening granularity (see the note above). Same guard
  // as the anchor snap below: once the user has worked the picker, the hook's
  // state is theirs.
  useEffect(() => {
    if (userTouched) return;
    setGranularity(initialGranularity);
  }, [initialGranularity, userTouched]);

  // Sort a copy so binary searches below are valid regardless of input order.
  const sorted = useMemo(() => {
    const arr = dataDates.slice();
    arr.sort((a, b) => a - b);
    return arr;
  }, [dataDates]);

  const latestMs = sorted.length > 0 ? sorted[sorted.length - 1] : null;
  const earliestMs = sorted.length > 0 ? sorted[0] : null;

  useEffect(() => {
    if (userTouched || latestMs == null) return;
    setAnchor(new Date(latestMs));
  }, [latestMs, userTouched]);

  const win = useMemo(() => windowForAnchor(granularity, anchor), [granularity, anchor]);

  // Largest data point strictly before win.start, smallest data point
  // at or after win.end. These are the targets prev/next snap to.
  const prevMs = useMemo<number | null>(() => {
    let lo = 0, hi = sorted.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (sorted[mid] < win.start) lo = mid + 1;
      else hi = mid;
    }
    return lo > 0 ? sorted[lo - 1] : null;
  }, [sorted, win.start]);

  const nextMs = useMemo<number | null>(() => {
    let lo = 0, hi = sorted.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (sorted[mid] < win.end) lo = mid + 1;
      else hi = mid;
    }
    return lo < sorted.length ? sorted[lo] : null;
  }, [sorted, win.end]);

  const allTime = granularity === 'All';
  const canPrev = !allTime && prevMs != null;
  const canNext = !allTime && nextMs != null;
  const isAtLatest = latestMs != null && latestMs >= win.start && latestMs < win.end;
  const hasData = sorted.length > 0;

  const setRange = (g: Granularity, a: Date) => {
    // When switching to a paged granularity, if (g, a) lands on a window
    // with no data, snap to the nearest data point. Keeps W/M/6M/Y from
    // dropping the user into an empty period after a granularity tab tap.
    let newAnchor = a;
    if (g !== 'All' && sorted.length > 0) {
      const w = windowForAnchor(g, a);
      let hasInWindow = false;
      for (const d of sorted) {
        if (d >= w.start && d < w.end) { hasInWindow = true; break; }
      }
      if (!hasInWindow) {
        const t = a.getTime();
        let best = sorted[0];
        let bestDist = Math.abs(best - t);
        for (let i = 1; i < sorted.length; i++) {
          const dist = Math.abs(sorted[i] - t);
          if (dist < bestDist) { bestDist = dist; best = sorted[i]; }
        }
        newAnchor = new Date(best);
      }
    }
    setGranularity(g);
    setAnchor(newAnchor);
    setUserTouched(true);
  };

  const pagePrev = () => {
    if (prevMs == null) return;
    setAnchor(new Date(prevMs));
    setUserTouched(true);
  };

  const pageNext = () => {
    if (nextMs == null) return;
    setAnchor(new Date(nextMs));
    setUserTouched(true);
  };

  const goLatest = () => {
    if (latestMs == null) return;
    setAnchor(new Date(latestMs));
    setUserTouched(true);
  };

  const goEarliest = () => {
    if (earliestMs == null) return;
    setAnchor(new Date(earliestMs));
    setUserTouched(true);
  };

  return {
    granularity, anchor, setRange,
    pagePrev, pageNext, goEarliest, goLatest,
    canPrev, canNext, isAtLatest, hasData,
  };
}
